import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Tag,
  Space,
  Typography,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Popconfirm,
  Select,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { BrandService } from '../services/brandService';
import { UserService } from '../services/userService';
import { Brand } from '../types';

const { Title, Text } = Typography;

const BrandsManagement: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [selectedBrandForUser, setSelectedBrandForUser] = useState<Brand | null>(null);
  const [creatingUser, setCreatingUser] = useState(false);
  const [form] = Form.useForm();
  const [userForm] = Form.useForm();
  const [users, setUsers] = useState<Array<{ id: string; email: string; name: string }>>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    loadBrands();
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const userList = await UserService.getAllUsers();
      setUsers(userList);
    } catch (error) {
      console.error('Failed to load users:', error);
      // Don't show error message, just log it
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadBrands = async () => {
    try {
      setLoading(true);
      const data = await BrandService.getAllBrands();
      setBrands(data);
    } catch (error) {
      message.error('Markalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEdit = () => {
    form.validateFields().then(async (values) => {
      try {
        if (editingBrand) {
          await BrandService.updateBrand(editingBrand.id, values);
          message.success('Marka güncellendi');
        } else {
          await BrandService.createBrand(values);
          message.success('Marka oluşturuldu');
        }
        setModalVisible(false);
        form.resetFields();
        setEditingBrand(null);
        loadBrands();
      } catch (error) {
        message.error('İşlem başarısız');
      }
    });
  };

  const handleEdit = (brand: Brand) => {
    setEditingBrand(brand);
    form.setFieldsValue(brand);
    setModalVisible(true);
  };

  const handleDelete = async (brandId: string) => {
    try {
      await BrandService.deleteBrand(brandId);
      message.success('Marka silindi');
      loadBrands();
    } catch (error) {
      message.error('Silme işlemi başarısız');
    }
  };

  const handleOpenCreateUserModal = (brand: Brand) => {
    setSelectedBrandForUser(brand);
    userForm.resetFields();
    setUserModalVisible(true);
  };

  const handleCreateManagerUser = async () => {
    try {
      const values = await userForm.validateFields();
      if (!selectedBrandForUser) {
        message.error('Marka seçilemedi');
        return;
      }

      setCreatingUser(true);
      const createdUser = await BrandService.createBrandManager(selectedBrandForUser.id, values);
      message.success(`${createdUser.name} ${values.role === 'agency' ? 'agency' : 'manager'} olarak eklendi`);
      setUserModalVisible(false);
      userForm.resetFields();
      setSelectedBrandForUser(null);
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Kullanıcı oluşturulamadı');
    } finally {
      setCreatingUser(false);
    }
  };

  const columns = [
    {
      title: 'Marka Adı',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'Website',
      dataIndex: 'website',
      key: 'website',
      render: (text: string) =>
        text ? (
          <a href={text} target="_blank" rel="noopener noreferrer">
            {text}
          </a>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'XML Feed',
      dataIndex: 'xmlFeedUrl',
      key: 'xmlFeedUrl',
      render: (text: string) =>
        text ? (
          <Tag color="blue">Yapılandırılmış</Tag>
        ) : (
          <Tag color="default">Yapılandırılmamış</Tag>
        ),
    },
    {
      title: 'API Key',
      dataIndex: 'apiKey',
      key: 'apiKey',
      render: (text: string) =>
        text ? (
          <Text code copyable>
            {text.substring(0, 20)}...
          </Text>
        ) : (
          <Tag color="red">Yok</Tag>
        ),
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) =>
        isActive ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Aktif
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="error">
            Pasif
          </Tag>
        ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: Brand) => (
        <Space>
          <Button
            type="link"
            icon={<UserAddOutlined />}
            onClick={() => handleOpenCreateUserModal(record)}
          >
            Kullanıcı Ekle
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Düzenle
          </Button>
          <Popconfirm
            title="Bu markayı silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Sil
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          marginBottom: 24,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <Title level={2}>Marka Yönetimi</Title>
          <Text type="secondary">Tüm markaları görüntüleyin ve yönetin</Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingBrand(null);
            form.resetFields();
            setModalVisible(true);
          }}
        >
          Yeni Marka
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={brands}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingBrand ? 'Markayı Düzenle' : 'Yeni Marka Ekle'}
        open={modalVisible}
        onOk={handleAddEdit}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingBrand(null);
        }}
        okText={editingBrand ? 'Güncelle' : 'Oluştur'}
        cancelText="İptal"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Marka Adı"
            rules={[{ required: true, message: 'Marka adı zorunludur' }]}
          >
            <Input placeholder="Örn: Kiğılı" />
          </Form.Item>

          <Form.Item
            name="website"
            label="Website"
            rules={[{ type: 'url', message: 'Geçerli bir URL girin' }]}
          >
            <Input placeholder="https://example.com" />
          </Form.Item>

          <Form.Item
            name="xmlFeedUrl"
            label="XML Feed URL"
            rules={[{ type: 'url', message: 'Geçerli bir URL girin' }]}
          >
            <Input placeholder="https://example.com/products.xml" />
          </Form.Item>

          <Form.Item name="apiKey" label="API Key">
            <Input.Password placeholder="API Key (opsiyonel)" />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Durum"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
          </Form.Item>

          {!editingBrand && (
            <Form.Item
              name="assignedEmails"
              label="Atanan Kullanıcılar"
              tooltip="Bu markaya erişebilecek kullanıcıları seçin"
            >
              <Select
                mode="multiple"
                placeholder="Kullanıcı seçin..."
                loading={loadingUsers}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={users.map((user) => ({
                  value: user.email,
                  label: `${user.email}${user.name ? ` (${user.name})` : ''}`,
                }))}
                style={{ width: '100%' }}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title={selectedBrandForUser ? `${selectedBrandForUser.name} için kullanıcı ekle` : 'Kullanıcı ekle'}
        open={userModalVisible}
        onOk={handleCreateManagerUser}
        confirmLoading={creatingUser}
        onCancel={() => {
          setUserModalVisible(false);
          setSelectedBrandForUser(null);
          userForm.resetFields();
        }}
        okText="Kullanıcı Ekle"
        cancelText="İptal"
        width={520}
      >
        <Form form={userForm} layout="vertical">
          <Form.Item
            name="role"
            label="Yetki"
            initialValue="manager"
            rules={[{ required: true, message: 'Yetki seçin' }]}
          >
            <Select options={[{ value: 'manager', label: 'Marka yöneticisi' }, { value: 'agency', label: 'Agency' }]} />
          </Form.Item>

          <Form.Item
            name="firstName"
            label="Ad"
            rules={[{ required: true, message: 'Ad zorunludur' }]}
          >
            <Input placeholder="Örn: Ahmet" />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="Soyad"
            rules={[{ required: true, message: 'Soyad zorunludur' }]}
          >
            <Input placeholder="Örn: Yılmaz" />
          </Form.Item>

          <Form.Item
            name="email"
            label="E-posta"
            rules={[
              { required: true, message: 'E-posta zorunludur' },
              { type: 'email', message: 'Geçerli bir e-posta girin' },
            ]}
          >
            <Input placeholder="ornek@marka.com" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Şifre"
            rules={[
              { required: true, message: 'Şifre zorunludur' },
              { min: 8, message: 'Şifre en az 8 karakter olmalı' },
            ]}
            extra="Agency kullanıcıları Studio içindeki Agency üretim ve test akışlarına erişebilir."
          >
            <Input.Password placeholder="Geçici şifre girin" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BrandsManagement;

